import React, { useMemo } from 'react';
import { ItemType, ProductSelection, ProductType } from 'components/ProductsSelection/types';
import { SelectedOptions, SelectedOptionsProps } from './SelectedOptions';
import { Widget } from '@spins/react-ui-library';
import { Button } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import './styles.scss';

interface Props {
  appliedSelection?: ProductSelection;
  selectedGroup?: string;
  currentBrand: string;
  clientId: string;
  weeks?: number;
  onRemoveAll?(currentProductType: ProductType): void;
  onRemoveItem?(currentProductType: ProductType, item: ItemType): void;
  onRemoveGroup?(): void;
  editable?: boolean;
  title?: string;
}

const Selections: React.FC<Props> = ({
  appliedSelection,
  selectedGroup,
  currentBrand,
  clientId,
  weeks,
  onRemoveAll,
  onRemoveItem,
  onRemoveGroup,
  editable = true,
  title = 'Selected',
}) => {
  const commonSelectedOptionsProps: Omit<
    SelectedOptionsProps,
    | 'loading'
    | 'total'
    | 'currentProductType'
    | 'label'
    | 'onRemoveAll'
    | 'onRemoveItem'
    | 'isGroupsFilter'
  > = useMemo(
    () => ({
      appliedSelection,
      brand: currentBrand,
      clientId,
      weeks,
      editable,
      title,
    }),
    [appliedSelection, clientId, currentBrand, weeks, editable, title]
  );

  const isSectionMapped = (section: Array<ItemType>) => {
    const notMappedItems = section.filter((item) => item.id === item.label);
    return !notMappedItems.length;
  };

  const areStoresMapped = isSectionMapped(appliedSelection.stores);
  const areItemsMapped = isSectionMapped(appliedSelection.items);

  return (
    <div className="selection-container">
      <Widget
        style={{
          border: 'none',
          boxShadow: 'none',
          width: '100%',
          height: '100%',
          overflow: 'auto',
        }}
      >
        {{
          header: {
            style: { border: 'none' },
            content: {
              left: <span> {title} </span>,
              leftStyle: { fontSize: '16px', fontWeight: 'normal' },
            },
          },
          body: {
            style: { overflow: 'auto' },
            content: (
              <div>
                {appliedSelection.accounts && appliedSelection.accounts.length > 0 && (
                  <SelectedOptions
                    {...commonSelectedOptionsProps}
                    loading={false}
                    total={appliedSelection.accounts.length}
                    currentProductType="accounts"
                    label="Accounts"
                    onRemoveAll={onRemoveAll}
                    onRemoveItem={onRemoveItem}
                    isGroupsFilter={!selectedGroup}
                    editable={editable}
                  />
                )}
                {appliedSelection.states && appliedSelection.states.length > 0 && (
                  <SelectedOptions
                    {...commonSelectedOptionsProps}
                    loading={false}
                    total={appliedSelection.states.length}
                    currentProductType="states"
                    label="States & Territories"
                    onRemoveAll={onRemoveAll}
                    onRemoveItem={onRemoveItem}
                    isGroupsFilter={!selectedGroup}
                    editable={editable}
                  />
                )}
                {appliedSelection.stores &&
                  appliedSelection.stores.length > 0 &&
                  areStoresMapped && (
                    <SelectedOptions
                      {...commonSelectedOptionsProps}
                      loading={false}
                      total={appliedSelection.stores.length}
                      currentProductType="stores"
                      label="Stores"
                      onRemoveAll={onRemoveAll}
                      onRemoveItem={onRemoveItem}
                      isGroupsFilter={!selectedGroup}
                      editable={editable}
                    />
                  )}
                {appliedSelection.items && appliedSelection.items.length > 0 && areItemsMapped && (
                  <SelectedOptions
                    {...commonSelectedOptionsProps}
                    loading={false}
                    total={appliedSelection.items.length}
                    currentProductType="items"
                    label="Items"
                    onRemoveAll={onRemoveAll}
                    onRemoveItem={onRemoveItem}
                    isGroupsFilter={!selectedGroup}
                    editable={editable}
                  />
                )}
                {selectedGroup && (
                  <div className="container">
                    <div className="group-title">
                      Groups
                      <Button className="btn_remove" onClick={() => onRemoveGroup()}>
                        <FontAwesomeIcon icon={faTimes} />
                      </Button>
                    </div>
                    <div className="group">{selectedGroup}</div>
                  </div>
                )}
              </div>
            ),
          },
        }}
      </Widget>
    </div>
  );
};

export default Selections;
